import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="flex min-h-screen bg-[#0B0F17] text-white">
      <div className="relative hidden w-1/2 overflow-hidden border-r border-slate-800 lg:block">
        <div className="grid-bg absolute inset-0 opacity-50" />
        <div className="absolute -bottom-20 -left-20 h-96 w-96 rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500 text-[#04120c]"><Mail className="h-5 w-5" /></div>
            <span className="display text-lg font-bold">RelayMX</span>
          </Link>
          <div>
            <h2 className="display text-3xl font-bold leading-tight">Ship transactional<br />email that lands.</h2>
            <p className="mt-4 max-w-sm text-slate-400">Domain verification, DKIM signing, SMTP credentials and a REST API — all in one dashboard.</p>
            <div className="mono mt-8 rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-xs text-emerald-400">
              $ relaymx domains verify yourcompany.com<br />
              <span className="text-slate-500">✓ SPF  ✓ DKIM  ✓ DMARC → verified</span>
            </div>
          </div>
          <span className="mono text-xs text-slate-600">smtp.relaymx.io</span>
        </div>
      </div>
      <div className="flex w-full items-center justify-center p-8 lg:w-1/2">
        <div className="w-full max-w-sm fade-up">
          <h1 className="display text-2xl font-bold">{title}</h1>
          <p className="mt-2 text-sm text-slate-400">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-300">{label}</span>
      <input {...props} className="w-full rounded-lg border border-slate-700 bg-slate-900/60 px-3.5 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-emerald-500/60 focus:ring-1 focus:ring-emerald-500/30" />
    </label>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your RelayMX dashboard">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
        <Field label="Password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
        <button data-testid="login-submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-500 py-2.5 font-semibold text-[#04120c] transition-colors hover:bg-emerald-400 disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
      </form>
      <div className="mono mt-6 rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
        Demo: <span className="text-emerald-400">demo@acmecorp.io</span> / <span className="text-emerald-400">demo123</span>
      </div>
      <p className="mt-6 text-center text-sm text-slate-400">
        No account? <Link to="/signup" className="font-medium text-emerald-400 hover:underline">Create one</Link>
      </p>
    </AuthShell>
  );
}

export { AuthShell };
